import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaLeaf, FaCheck, FaPlay, FaClock, FaUsers, FaShieldAlt, FaHandsHelping, FaLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import bemerImage from '../../components/assets/Staff/Bemer 2.jpg';
import bemer1Image from '../../components/assets/Staff/Bemer 1.jpg';
import bemer3Image from '../../components/assets/Staff/Bemer 3.jpg';

const BEMERSessions = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const howItWorksRef = useRef(null);
  const benefitsRef = useRef(null);
  const whoRef = useRef(null);
  const approachRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'how-it-works': howItWorksRef,
      'benefits': benefitsRef,
      'who': whoRef,
      'approach': approachRef
    };

    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'how-it-works', ref: howItWorksRef },
        { id: 'benefits', ref: benefitsRef },
        { id: 'who', ref: whoRef },
        { id: 'approach', ref: approachRef }
      ];

      const scrollPosition = window.scrollY + 100;

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Overview', icon: FaHeart },
    { id: 'session', label: 'Session Experience', icon: FaPlay },
    { id: 'how-it-works', label: 'How It Works', icon: FaShieldAlt },
    { id: 'benefits', label: 'Benefits', icon: FaLeaf },
    { id: 'who', label: 'Who It\'s For', icon: FaUsers },
    { id: 'approach', label: 'Our Approach', icon: FaHandsHelping }
  ];

  const overviewFeatures = [
    {
      icon: FaHeart,
      title: "Gentle & Non-Invasive",
      description: "No pressure, manipulation, or effort — just space for your body to respond"
    },
    {
      icon: FaShieldAlt,
      title: "Patented European Technology",
      description: "Low-frequency electromagnetic signal to support microcirculation"
    },
    {
      icon: FaLeaf,
      title: "Deeply Restorative",
      description: "Subtle, effortless support for your body's natural self-regulating processes"
    }
  ];

  const benefits = [
    "Feeling less depleted during the day, with more natural, steady energy",
    "Winding down more easily in the evening and waking up feeling more refreshed",
    "A smoother sense of recovery after physical or emotional demands",
    "Reduced sensations of heaviness, internal tension, or overload",
    "A deeper feeling of calm, grounding, and presence in the body"
  ];

  const whoItsFor = [
    "Those who feel fatigued, overwhelmed, or out of balance",
    "Anyone experiencing ongoing stress or difficulty switching off",
    "People who value gentle, non-invasive approaches to wellbeing",
    "Those who wish to complement an existing wellness or self-care routine",
    "Anyone curious about combining modern technology with holistic support"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="BEMER Sessions" description="BEMER therapy at Bioresonance Leicester – patented microcirculation support for natural rhythm and recovery. Gentle, non-invasive. Book at 108 New Walk." path="/therapy/bemer-sessions" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">BEMER Sessions</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHandsHelping className="mr-2" />
                Gentle Support for Your Body's Natural Rhythm
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                BEMER Sessions
              </h1>

              <p className="text-xl text-dark-grey leading-relaxed">
                Modern life places constant demands on the body — continuous stimulation, pressure to perform, and very little time to truly reset. Over time, this can leave you feeling drained, tense, or out of sync with yourself.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed">
                BEMER sessions offer a calm, non-invasive way to support your body in reconnecting with its natural rhythm. This is not about fixing, forcing, or pushing change. It's about creating the right conditions for your body to do what it already knows how to do — with greater ease, flow, and balance.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>Peaceful, comfortable environment</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaLeaf className="text-gold mr-2" />
                  <span>Fully clothed, simply rest</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>

                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={bemerImage}
                      alt="BEMER Sessions"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What Makes BEMER Special
              </h2>
              <p className="text-lg text-gray-600">
                Using patented European technology, BEMER sessions gently support microcirculation, encouraging the body's natural self-regulating processes. The experience is subtle, effortless, and deeply restorative.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {overviewFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-grey/20"
                >
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="text-2xl text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-dark-grey mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Session Experience */}
      <section ref={sessionRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                  What is a session like?
                </h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Each session takes place in a peaceful, comfortable environment designed to help you slow down and switch off.
                  </p>
                  <p>
                    You simply lie down, fully clothed, and allow yourself to rest. The system delivers a low-frequency electromagnetic signal created to support healthy blood flow in the smallest vessels of the body.
                  </p>
                  <p>
                    This gentle stimulation supports the natural delivery of oxygen and nutrients to your cells, while assisting everyday metabolic processes — without pressure, manipulation, or effort.
                  </p>
                  <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                    <p className="text-dark-grey font-semibold text-lg">
                      There is nothing you need to do. Just space, stillness, and time for your body to respond in its own way.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="relative group">
                  <div className="relative overflow-hidden rounded-3xl h-80">
                    <img
                      src={bemer1Image}
                      alt="BEMER session experience"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works / Every body responds differently */}
      <section ref={howItWorksRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Every body responds differently
              </h2>
              <p className="text-lg text-gray-600">
                Some people notice subtle shifts after their first session, while for others the experience unfolds gradually over time.
              </p>
            </div>

            <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
              <p className="text-gray-600 leading-relaxed text-center">
                This is an experience for people who appreciate subtlety, consistency, and depth — rather than quick fixes or promises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section ref={benefitsRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                What you may experience
              </h2>
              <p className="text-lg text-gray-600">
                BEMER sessions may support your wellbeing in many ways. For many, regular sessions become a quiet yet powerful part of their personal wellbeing routine.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-grey/20">
                <h3 className="text-2xl font-bold text-dark-grey mb-6">BEMER sessions may support:</h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{benefit}</p>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="relative overflow-hidden rounded-3xl h-80">
                  <img
                    src={bemer3Image}
                    alt="BEMER natural support"
                    className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                </div>
                <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section ref={whoRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Who is this for?
              </h2>
              <p className="text-lg text-gray-600">
                BEMER sessions are especially suited for those who:
              </p>
            </div>

            <div className="space-y-4">
              {whoItsFor.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-6 h-6 bg-blue/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <FaUsers className="text-blue text-sm" />
                  </div>
                  <p className="text-gray-600">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section ref={approachRef} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaLightbulb className="mr-2" />
                Holistic Wellbeing
              </div>
              <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                Our approach
              </h2>
            </div>

            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                My work is rooted in respect for the body's innate intelligence and its capacity for self-regulation.
              </p>
              <p>
                BEMER sessions are offered as part of a holistic wellbeing approach, focused on balance, restoration, and creating supportive conditions for the body — not on diagnosing, treating, or replacing medical care.
              </p>
              <p>
                Each session is delivered with care, presence, and attention to individual experience, recognising that wellbeing is deeply personal and never one-size-fits-all.
              </p>
              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
                <p className="text-dark-grey font-semibold text-lg text-center">
                  This is not about becoming someone else. It's about giving your body the space it needs to function with greater ease — and allowing yourself to feel more at home in it again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Experience BEMER Sessions?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            Discover how this gentle, non-invasive technology can support your body's natural rhythm and help you feel more at home in yourself again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default BEMERSessions;
